/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { Switch } from '@coursera-web/cds-labs';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { InlineNotification, Typography2 } from '@coursera/cds-core';

import {
  getHighlightSettings,
  updateHighlightSettings,
} from 'bundles/account-settings/actions/HighlightSettingsActions';
import { UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import HighlightSettingsStore from 'bundles/account-settings/stores/HighlightSettingsStore';

import _t from 'i18n!nls/account-settings';

type OuterProps = {
  userId: number;
};

type InnerProps = {
  disableDataAggregation: boolean;
  disableTopHighlightsDisplay: boolean;
  updateStatus: string;
} & OuterProps;

const DESCRIPTION_ID = 'notes-highlights-description';

const styles = {
  card: css`
    display: flex;
    flex-direction: column;
  `,
  notification: css`
    margin-top: var(--cds-spacing-100);
  `,
  row: css`
    display: flex;
    gap: var(--cds-spacing-150);
    align-items: flex-start;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    flex: 1 1 0;
    min-width: 0;
  `,
  switchWrapper: css`
    flex-shrink: 0;
  `,
};

export const NotesAndHighlightingSettings = ({
  userId,
  disableDataAggregation,
  disableTopHighlightsDisplay,
  updateStatus,
}: InnerProps) => {
  const executeAction = useFluxibleAction();

  const [localDisable, setLocalDisable] = useState(disableDataAggregation);
  const [notificationStatus, setNotificationStatus] = useState('');
  const enabled = !localDisable;
  const isUpdating = updateStatus === UpdateStates.UPDATING;

  React.useEffect(() => {
    if (updateStatus === UpdateStates.SUCCEEDED || updateStatus === UpdateStates.FAILED) {
      setNotificationStatus(updateStatus);
    }
  }, [updateStatus]);

  const onToggle = (newSelected: boolean) => {
    setNotificationStatus(''); // clear before each new save
    const newDisable = !newSelected;
    setLocalDisable(newDisable);
    executeAction(updateHighlightSettings, {
      userId: String(userId),
      disableDataAggregation: newDisable,
      disableTopHighlightsDisplay,
    });
  };

  return (
    <div css={styles.card}>
      <div css={styles.row}>
        <div css={styles.content}>
          <Typography2 variant="subtitleLarge" component="h2">
            {_t('Notes and highlights')}
          </Typography2>
          <Typography2 variant="bodySecondary" component="p" color="supportText" id={DESCRIPTION_ID}>
            {_t(
              `Your highlighting and note-taking activity can be used in combination with that of other
            learners in the course to determine the most highlighted phrases.
            No personal information is displayed to other learners.`
            )}
          </Typography2>
        </div>
        <div css={styles.switchWrapper}>
          <Switch
            selected={enabled}
            onToggle={onToggle}
            disabled={isUpdating}
            hideTooltip
            aria-label={_t('Notes and highlights')}
            aria-describedby={DESCRIPTION_ID}
          />
        </div>
      </div>
      <div role="status" aria-live="polite" aria-atomic="true">
        {notificationStatus === UpdateStates.SUCCEEDED && (
          <InlineNotification css={styles.notification} severity="success">
            {_t('Update successful!')}
          </InlineNotification>
        )}
      </div>
      <div role="alert" aria-live="assertive" aria-atomic="true">
        {notificationStatus === UpdateStates.FAILED && (
          <InlineNotification css={styles.notification} severity="error">
            {_t('Update failed. Please try again.')}
          </InlineNotification>
        )}
      </div>
    </div>
  );
};

const NotesAndHighlightingSettingsWrapper = ({ userId }: OuterProps) => {
  const executeAction = useFluxibleAction();

  const { disableDataAggregation, disableTopHighlightsDisplay, updateStatus, loaded } = useFluxibleStore(
    HighlightSettingsStore,
    (store) => ({
      disableDataAggregation: store.getDisableDataAggregation(),
      disableTopHighlightsDisplay: store.getDisableTopHighlightsDisplay(),
      updateStatus: store.getUpdateStatus(),
      loaded: store.hasLoaded(),
    })
  );

  useEffect(() => {
    if (userId != null) {
      executeAction(getHighlightSettings, { userId: String(userId) });
    }
  }, [executeAction, userId]);

  if (!loaded) {
    return null;
  }

  return (
    <NotesAndHighlightingSettings
      userId={userId}
      disableDataAggregation={disableDataAggregation}
      disableTopHighlightsDisplay={disableTopHighlightsDisplay}
      updateStatus={updateStatus}
    />
  );
};

export default NotesAndHighlightingSettingsWrapper;
